/**
 * ArtifactsPage — Artefakt-Inventur als MicroProcessFlow.
 *
 * **Anbindung an die globale Formatversion (AP-B).** Die Wahl in der Kopfzeile
 * schneidet die Tabelle auf einen Stand zu: aus drei Slot-Spalten wird eine.
 *
 * Zwei Typen liegen aber gar nicht auf der FV-Achse — `bnetza-festlegung`
 * (GPKE/WiM/MaBiS/BELUGA/fristen) und `bo4e-schema` (unbefristet). Sie dürfen
 * bei einer FV-Wahl nicht verschwinden; genau dafür lässt `matchesRelease`
 * einen Kandidaten ohne FV-Bezug (`null`) immer durch. Damit das kein stummes
 * Stehenbleiben ist, tragen diese Zeilen bei gewähltem Stand sichtbar
 * „nicht releasegebunden" — sonst läse man ihre Ghost-Linie als „für diesen
 * Stand nichts da", und das wäre eine falsche Aussage.
 */
import { Badge, Card } from "@conuti-das/prince-ui";
import { matchesRelease, releaseLabel, useRelease } from "@/context";
import {
  MicroFlowHeader,
  MicroFlowRow,
  formatCount,
  type FlowCells,
  type FlowSlotDef,
} from "@/components/flow";
import artifactsData from "@/data/generated/artifacts.json";
import { ALL, type ReleaseKey } from "@/types/pipeline";
import type { ArtifactIndex } from "@/types";

const TYPE_INFO: Record<string, { label: string; sub: string }> = {
  ebds: { label: "EBDs", sub: "Entscheidungsbaumdiagramme" },
  ahb: { label: "AHB", sub: "Anwendungshandbuch — Prüfidentifikatoren" },
  mig: { label: "MIG", sub: "Message Implementation Guide" },
  pruefidentifikatoren: { label: "Prüfidentifikatoren", sub: "Steuerzentrale der Prozess-Tabellen" },
  codelisten: { label: "Codelisten", sub: "OBIS-Kennzahlen, Transaktionsgründe" },
  "bnetza-festlegung": { label: "BNetzA-Prozesse", sub: "GPKE, WiM, MaBiS, BELUGA (Festlegungen)" },
  schemas: { label: "Schemas", sub: "XML-Schemas / XSD" },
  "bo4e-schema": { label: "BO4E-Schemas", sub: "Business Objects Energie" },
};

// Nur diese Typen haben (bislang) eine Detail-/Viewer-Seite unter /artifacts/:type.
const DRILLABLE = new Set(["ebds", "ahb", "mig", "pruefidentifikatoren", "codelisten"]);

/** Die drei numerischen Stände sind die festen Slots der Artefakt-Kette. */
export const RELEASE_SLOTS: FlowSlotDef[] = [
  { id: "202604", label: "202604", full: "Formatversion 202604" },
  { id: "202610", label: "202610", full: "Formatversion 202610" },
  { id: "202710", label: "202710", full: "Formatversion 202710" },
];

/**
 * Eine Artefakt-Kette über die drei Stände.
 *
 *  - Stand fehlt für diesen Typ  → Slot bleibt leer  → **Ghost-Linie**
 *    („trifft nicht zu"). Genau dafür ist das Ghost-Prinzip gedacht:
 *    `bnetza-festlegung` und `bo4e-schema` kennen keinen der drei Stände und
 *    zeigen deshalb in allen drei Slots die reine Hairline.
 *  - Konsultationsfassung        → **Siegel** („liegt nicht bei der Maschine")
 *  - Zähler 0                    → **leerer Ring mit „0"** — vorhanden, aber leer;
 *    das ist eine andere Aussage als „gibt es hier nicht".
 */
export function artifactCells(
  releases: Record<string, number>,
  consultation: ReadonlySet<string>,
): FlowCells {
  const cells: FlowCells = {};
  for (const slot of RELEASE_SLOTS) {
    if (!Object.prototype.hasOwnProperty.call(releases, slot.id)) continue; // → Ghost
    const n = releases[slot.id];
    cells[slot.id] = {
      state: consultation.has(slot.id) ? "sealed" : n > 0 ? "ok" : "open",
      value: n,
    };
  }
  return cells;
}

/** Nicht-numerische Stände (`GPKE`, `WiM`, `unbefristet`, …) als Klartext. */
export function specialReleases(releases: Record<string, number>): string {
  const known = new Set(RELEASE_SLOTS.map((s) => s.id));
  const extra = Object.keys(releases).filter((k) => !known.has(k));
  return extra.map((k) => `${k} · ${formatCount(releases[k])}`).join(" · ");
}

/**
 * Liegt dieser Typ auf der FV-Achse? Der Kandidat, den `matchesRelease` sieht:
 * ein sechsstelliger Stand — oder `null` für „gehört gar nicht dazu".
 *
 * `bnetza-festlegung` zählt nach Festlegung (GPKE, WiM, …), `bo4e-schema` nach
 * `unbefristet`. Beide haben keinen einzigen FV-Schlüssel und sind damit
 * nicht releasegebunden.
 */
export function releaseCandidates(releases: Record<string, number>): string[] | null {
  const fvs = Object.keys(releases).filter((k) => /^\d{6}$/.test(k));
  return fvs.length > 0 ? fvs : null;
}

/**
 * Bleibt die Zeile bei dieser Wahl sichtbar? Nicht releasegebundene Typen
 * immer — das ist der `null`-Durchlass von `matchesRelease`, kein Sonderfall.
 * Releasegebundene Typen ohne den gewählten Stand bleiben ebenfalls stehen:
 * ihre Ghost-Linie sagt „diesen Stand gibt es hier nicht", und diese Aussage
 * geht verloren, wenn die Zeile verschwindet.
 */
export function rowMatches(
  release: ReleaseKey,
  releases: Record<string, number>,
): boolean {
  const candidates = releaseCandidates(releases);
  if (candidates === null) return matchesRelease(release, null);
  return true;
}

/**
 * Die Slot-Spalten für die aktuelle Wahl: alle drei bei `ALL`, sonst genau
 * die gewählte. Kennt dieser Build den Stand nicht als Slot, bleiben alle
 * stehen — eine leere Kette wäre schlimmer als eine zu breite.
 */
export function visibleSlots(release: ReleaseKey): FlowSlotDef[] {
  if (release === ALL) return RELEASE_SLOTS;
  const only = RELEASE_SLOTS.filter((s) => s.id === release);
  return only.length > 0 ? only : RELEASE_SLOTS;
}

/**
 * Artefakte als MicroProcessFlow (design-hig.md ③): eine Zeile je Typ, drei
 * Stand-Slots mit dem Zähler unter dem Knoten. Die Stand-Namen stehen genau
 * einmal, in der Kopfzeile.
 */
export function ArtifactsPage() {
  const data = artifactsData as ArtifactIndex;
  const counts = data.counts;
  const { release } = useRelease();
  const slots = visibleSlots(release);
  const types = Object.keys(TYPE_INFO).filter((t) => rowMatches(release, counts[t] ?? {}));

  return (
    <div className="page">
      <div className="page-header">
        <h2 className="page-title">Artefakte</h2>
        <div className="page-subtitle">
          Strukturierte Extrakte aus der maco-knowledge-collection. Klicke einen Typ, um die
          konkreten Artefakte zu sehen.
        </div>
      </div>

      <Card padding="none" className="mpf-table mpf-artifacts">
        <MicroFlowHeader slots={slots} slotWidth={44} trail="Summe">
          Artefakt-Typ
        </MicroFlowHeader>
        {types.map((type) => {
          const info = TYPE_INFO[type];
          const releases = counts[type] ?? {};
          const total = Object.values(releases).reduce((a, b) => a + b, 0);
          const consultation = new Set(data.consultationReleases?.[type] ?? []);
          const special = specialReleases(releases);
          const unbound = releaseCandidates(releases) === null;
          return (
            <MicroFlowRow
              key={type}
              slots={slots}
              slotWidth={44}
              cells={artifactCells(releases, consultation)}
              // Eigenes Vokabular: hier heißt das Siegel „Konsultationsfassung"
              // und der leere Ring „vorhanden, aber leer" — ein Artefakt-Stand
              // wartet nicht auf einen Menschen.
              vocabulary="artifact"
              label={`${info.label}, ${formatCount(total)} Artefakte`}
              title={info.label}
              subtitle={special ? `${info.sub} — Sonderstände: ${special}` : info.sub}
              // Nur Typen mit Viewer-Seite sind Links; die übrigen Zeilen
              // bleiben bewusst reine Information (kein Tab-Stopp ins Leere).
              href={DRILLABLE.has(type) ? `/artifacts/${type}` : undefined}
              trail={
                <>
                  <span className="mpf-total">{formatCount(total)} ges.</span>
                  {/* Bei gewähltem Stand sagt die Zeile selbst, dass sie nicht
                      auf dieser Achse liegt — sonst läse man die Ghost-Linie
                      als „für diesen Stand nichts extrahiert". */}
                  {unbound && release !== ALL && (
                    <Badge tone="gray" variant="soft">nicht releasegebunden</Badge>
                  )}
                  {/* Hier stand der Ziel-Badge des Artefakt-Typs. Die acht
                      Artefakt-Ziele stehen jetzt im Abschnitt „Artefakt-Typen"
                      auf `/umsetzungsgrad`. */}
                </>
              }
            />
          );
        })}
      </Card>

      <p className="page-subtitle" style={{ marginTop: "var(--s-3)" }}>
        {release === ALL
          ? "Alle Stände — die Formatversion wird in der Kopfzeile gewählt."
          : `${releaseLabel(release)} — Auswahl in der Kopfzeile. BNetzA-Prozesse und` +
            " BO4E-Schemas liegen nicht auf der Formatversions-Achse und bleiben deshalb stehen."}
      </p>
    </div>
  );
}
